import { FC, useContext, useEffect, useState } from "react";

import LocationContext from "@context/location/locationContext";
import ShowErrorToastContext from "@context/showErrorToast/showErrorToastContext";
import { DEVICE, useWindowSizeChanged } from "@hooks/DeviceSize";
import { TABLE_ROW_COUNT } from "@src/constants";
import Filter from "./Filter";
import NotificationTable from "./NotificationTable";
import {
  getNotificationFilter,
  getNotificationList,
  initialLoderState,
} from "./helper";
import { ListWrapper } from "./style";

interface NotificationListType {
  handleViewDetailsButton: (
    propertyShortName: string,
    checkNo: string,
    notificationId: string
  ) => void;
  filterType: NotificationFitlerType[];
  handleDelete: (e: string) => void;
  handleClick: (e: string) => void;
  unreadOnly?: boolean;
  count: number;
}

const NotificationList: FC<NotificationListType> = ({
  handleViewDetailsButton,
  filterType,
  handleClick,
  handleDelete,
  unreadOnly = false,
  count,
}) => {
  const [notificationList, setNotificationList] = useState<
    NotificationDataType[]
  >([]);

  const [loading, setLoading] = useState<LoadingType>(initialLoderState);
  const [showLoadMore, setShowLoadMore] = useState<boolean>(false);

  const [{ locationState }] = useContext(LocationContext);
  const { property, store, revenueCenter, date } = locationState;
  const [{ showErrorToastSetState }] = useContext(ShowErrorToastContext);
  const [filters, setFilters] = useState({
    property,
    store,
    rvc: revenueCenter,
    date,
    filterBy: getNotificationFilter(filterType),
    page: 1,
    unreadOnly,
  });

  const getData = async (
    customFilter: CustomNotificationFilterType,
    list: NotificationDataType[]
  ) => {
    const res = await getNotificationList(customFilter);
    if (!res?.error) {
      setNotificationList([...list, ...res.data]);
      setShowLoadMore(
        res?.data?.length === TABLE_ROW_COUNT &&
          Number(res?.data[0]?.total) !== [...list, ...res.data]?.length
      );
    } else {
      showErrorToastSetState({
        errorMessage: res?.error || "",
        errorTitle: "Unable to Fetch Notification",
        open: true,
      });
    }
    setLoading((prev) => {
      return {
        ...prev,
        loadMoreLoader: false,
        mainLoader: false,
      };
    });
  };

  const handleLoadMore = () => {
    setLoading((prev) => {
      return {
        ...prev,
        loadMoreLoader: true,
      };
    });
    const customFilter: CustomNotificationFilterType = {
      ...filters,
      page: filters.page + 1,
    };
    setFilters(customFilter);
    getData(customFilter, notificationList);
  };

  useEffect(() => {
    setLoading((prev) => {
      return {
        ...prev,
        mainLoader: true,
      };
    });
    const customFilter: CustomNotificationFilterType = {
      property,
      store,
      rvc: revenueCenter,
      date,
      filterBy: getNotificationFilter(filterType),
      page: 1,
      unreadOnly,
    };
    setFilters(customFilter);
    getData(customFilter, []);
  }, [unreadOnly, filterType, property, store, revenueCenter, date, count]);

  const device = useWindowSizeChanged();
  const isDesktop = device === DEVICE.DESKTOP;

  return (
    <ListWrapper isDesktop={isDesktop}>
      <Filter
        filterType={filterType}
        handleClick={handleClick}
        handleDelete={handleDelete}
      />
      <NotificationTable
        loading={loading}
        showLoadMore={showLoadMore}
        notificationList={notificationList}
        handleViewDetailsButton={handleViewDetailsButton}
        handleLoadMore={handleLoadMore}
      />
    </ListWrapper>
  );
};

NotificationList.defaultProps = {
  unreadOnly: false,
};

export default NotificationList;
